import { type SchoolDataMigration } from '@/routes/data-migrations/types';
import { Refresh as RetryIcon } from '@mui/icons-material';
import { useState } from 'react';
import api from '@/lib/api';
import styled from '@emotion/styled';
import { Button, TextField } from '@mui/material';
import Space from '@/components/Space';

type RetryDataMigrationProps = {
  dataMigration: SchoolDataMigration;
};

const RetryFormWrapper = styled('form')(({ theme }) => ({
  marginTop: 24,
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
}));

const RetryDataMigration = ({ dataMigration }: RetryDataMigrationProps) => {
  const [legacyDatabaseServiceAccount, setLegacyDatabaseServiceAccount] =
    useState('');
  const [
    legacyDatabaseServiceAccountError,
    setLegacyDatabaseServiceAccountError,
  ] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [hasAttemptedRetry, setHasAttemptedRetry] = useState(false);

  const handleRetry = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    setLegacyDatabaseServiceAccountError(null);

    if (legacyDatabaseServiceAccount.trim().length === 0) {
      setLegacyDatabaseServiceAccountError('Please enter a service account');

      return;
    }

    setLoading(true);

    await api.post(`/data-migrations/${dataMigration.id}/retry`, {
      legacyDatabaseServiceAccount,
    });

    setLoading(false);
    setHasAttemptedRetry(true);
  };

  if (hasAttemptedRetry) {
    return null;
  }

  return (
    <RetryFormWrapper onSubmit={handleRetry}>
      <TextField
        label="Legacy database service account"
        name="legacy_database_service_account"
        value={legacyDatabaseServiceAccount}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setLegacyDatabaseServiceAccount(e.target.value)
        }
        type="password"
        error={legacyDatabaseServiceAccountError !== null}
        helperText={legacyDatabaseServiceAccountError}
        size="small"
        disabled={loading}
      />
      <Space width={12} />
      <Button
        variant="contained"
        startIcon={<RetryIcon />}
        loadingPosition="start"
        type="submit"
        loading={loading}
        disableElevation
      >
        Retry
      </Button>
    </RetryFormWrapper>
  );
};

export default RetryDataMigration;
